import React, { useState } from 'react';
import useGameStore from '../../store/gameStore';
import { VisuelMagasin } from './VisuelMagasin';
import {
  CATEGORIES_AMELIORATIONS, AMELIORATIONS, peutAcheter, coutNiveau,
  entretienNiveau, computeAmelFx, chantiersEnCours,
} from '../../utils/ameliorations';
import { formatMoney } from '../../utils/format';
import { toast } from '../ui/sonner';
import { HistoriqueInvestissements } from './HistoriqueInvestissements';
import {
  Tags, LayoutGrid, Megaphone, Sparkles, Lightbulb, Package, CreditCard,
  ScanLine, ShoppingBag, Wrench, Wine, Tag, MonitorPlay, Lock, TrafficCone, Hammer, LineChart,
} from 'lucide-react';

const ICONES = {
  facing_promo: Tags, tetes_gondole: LayoutGrid, plv: Megaphone, vitrine_plus: Sparkles,
  eclairage_led: Lightbulb, reserve_plus: Package, caisse_sup: CreditCard, caisses_auto: ScanLine,
  click_collect: ShoppingBag, sav: Wrench, degustation: Wine, etiquettes: Tag, ecrans: MonitorPlay,
};

const fmtEffets = (e = {}) => [
  e.trafic_pct && `Trafic +${e.trafic_pct} %`,
  e.conversion_pct && `Conversion +${e.conversion_pct} %`,
  e.panier_pct && `Panier +${e.panier_pct} %`,
  e.satisfaction_pts && `Satisfaction +${e.satisfaction_pts} pts`,
  e.capacite_reserve_pct && `Réserve +${e.capacite_reserve_pct} %`,
  e.capacite_caisse && `Caisse +${e.capacite_caisse} clients/j`,
  e.part_cc_pct && `${e.part_cc_pct} % de retraits en magasin`,
].filter(Boolean).join(' · ');

const RAISONS = {
  type: 'Réservé aux magasins moyens et grandes surfaces',
  secteur: 'Réservé au secteur caviste',
  chantier: 'Travaux déjà en cours',
};

/** SCD R3 — Écran « Évolution du magasin » : visuel + bandeau équipements + arbre d'améliorations. */
export const EvolutionMagasin = () => {
  const retail = useGameStore(s => s.retail);
  const company = useGameStore(s => s.company);
  const treasury = useGameStore(s => s.treasury);
  const totalDays = useGameStore(s => s.totalDays) || 0;
  const acheterAmelioration = useGameStore(s => s.acheterAmelioration);
  const [magId, setMagId] = useState('principal');
  const [hoverId, setHoverId] = useState(null);
  const [tab, setTab] = useState('arbre');

  const magasins = [{ ...retail.magasin, id: 'principal', principal: true }, ...(retail.magasins || [])];
  const mag = magasins.find(m => m.id === magId) || magasins[0];
  const etat = mag.ameliorations || {};
  const chantiers = chantiersEnCours(mag, totalDays);
  const fx = computeAmelFx(mag);
  const acquises = AMELIORATIONS.filter(a => (etat[a.id] || 0) > 0);
  const HoverIcon = hoverId ? ICONES[hoverId] : null;

  return (
    <div className="surface-card p-4" data-testid="evolution-magasin">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase flex items-center gap-2">
          <Hammer className="w-4 h-4 text-accent" /> Évolution du magasin
        </h3>
        <div className="flex gap-1 rounded-lg bg-ink-900/60 border border-ink-600 p-0.5">
          <button type="button" onClick={() => setTab('arbre')}
            className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-colors ${tab === 'arbre' ? 'bg-accent text-white' : 'text-slate-400 hover:text-slate-200'}`}
            data-testid="tab-arbre">
            🛠️ Améliorations
          </button>
          <button type="button" onClick={() => setTab('roi')}
            className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-colors flex items-center gap-1 ${tab === 'roi' ? 'bg-accent text-white' : 'text-slate-400 hover:text-slate-200'}`}
            data-testid="tab-historique-roi">
            <LineChart className="w-3 h-3" /> Historique & ROI
          </button>
        </div>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {magasins.map(m => (
            <button key={m.id} type="button" onClick={() => setMagId(m.id)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${mag.id === m.id ? 'bg-accent text-white border-accent' : 'border-ink-600 text-slate-400 hover:border-accent/60'}`}
              data-testid={`evolution-select-${m.id}`}>
              {m.principal ? '🏬 Principal' : `🏪 ${m.nom || m.nomType}`}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Visuel + bandeau équipements */}
        <div className="lg:col-span-2 space-y-2">
          <div className="relative rounded-lg overflow-hidden">
            {mag.typeId && mag.localisationId ? (
              <VisuelMagasin localisationId={mag.localisationId} typeId={mag.typeId}
                secteurId={retail.secteur} positionnement={retail.positionnement} className="w-full border border-ink-600" testId="evolution-visuel" />
            ) : (
              <div className="w-full h-40 bg-ink-900/60 border border-dashed border-ink-600 rounded-lg flex items-center justify-center text-slate-500 text-xs">Visuel indisponible</div>
            )}
            {chantiers.length > 0 && (
              <div className="absolute inset-0 bg-ink-950/60 backdrop-blur-[1px] flex flex-col items-center justify-center gap-1.5" data-testid="chantier-overlay">
                <TrafficCone className="w-8 h-8 text-amber-400 animate-bounce" />
                {chantiers.map(c => {
                  const amelio = AMELIORATIONS.find(a => a.id === c.amelioId);
                  const pct = Math.round(((totalDays - c.debutJour) / Math.max(1, c.finJour - c.debutJour)) * 100);
                  return (
                    <div key={`${c.amelioId}-${c.finJour}`} className="w-3/4 text-center">
                      <p className="text-[11px] font-bold text-amber-300">
                        🚧 {amelio?.nom} — reste {c.finJour - totalDays} j{c.fermeturePartielle ? ' · magasin partiellement fermé (-30 % trafic)' : ''}
                      </p>
                      <div className="h-1.5 rounded-full bg-ink-800 overflow-hidden mt-1">
                        <div className="h-full bg-amber-400 transition-all duration-700" style={{ width: `${Math.max(5, pct)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="bandeau-equipements">
            {acquises.length === 0 && chantiers.length === 0 && !hoverId && (
              <p className="text-[11px] text-slate-500">Aucun équipement : survolez une amélioration pour la prévisualiser.</p>
            )}
            {acquises.map(a => {
              const Ico = ICONES[a.id] || Tags;
              return (
                <span key={a.id} className="inline-flex items-center gap-1 rounded-full bg-accent/10 border border-accent/40 text-accent px-2 py-0.5 text-[10px] font-bold animate-in fade-in zoom-in duration-500" data-testid={`equipement-${a.id}`}>
                  <Ico className="w-3 h-3" /> {a.nom}{a.niveaux.length > 1 ? ` niv. ${etat[a.id]}` : ''}
                </span>
              );
            })}
            {chantiers.map(c => (
              <span key={c.amelioId} className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 border border-amber-500/50 text-amber-400 px-2 py-0.5 text-[10px] font-bold animate-pulse" data-testid={`equipement-chantier-${c.amelioId}`}>
                🚧 {AMELIORATIONS.find(a => a.id === c.amelioId)?.nom}
              </span>
            ))}
            {HoverIcon && !(etat[hoverId] > 0) && !chantiers.some(c => c.amelioId === hoverId) && (
              <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-500 text-slate-400 px-2 py-0.5 text-[10px] font-bold opacity-70" data-testid="apercu-equipement">
                <HoverIcon className="w-3 h-3" /> {AMELIORATIONS.find(a => a.id === hoverId)?.nom} (aperçu)
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-1.5 font-mono text-[11px] text-slate-400 pt-2 border-t border-ink-700" data-testid="effets-ameliorations">
            <p>Trafic : <span className="text-emerald-400">+{fx.traficPct} %</span></p>
            <p>Conversion : <span className="text-emerald-400">+{fx.conversionPct} %</span></p>
            <p>Panier : <span className="text-emerald-400">+{fx.panierPct} %</span></p>
            <p>Réserve : <span className="text-slate-200">+{fx.capaciteReservePct} %</span></p>
            <p>Caisse : <span className="text-slate-200">+{fx.capaciteCaisse} clients/j</span></p>
            <p>Entretien : <span className="text-amber-400">{formatMoney(Math.round(fx.entretienJour * 30))}/mois</span></p>
          </div>
        </div>

        {/* Arbre d'améliorations ou Historique & ROI */}
        <div className="lg:col-span-3 space-y-3">
          {tab === 'roi' && <HistoriqueInvestissements />}
          {tab === 'arbre' && CATEGORIES_AMELIORATIONS.map(cat => (
            <div key={cat.id} data-testid={`categorie-${cat.id}`}>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">{cat.emoji} {cat.nom}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {AMELIORATIONS.filter(a => a.categorie === cat.id).map(a => {
                  const Ico = ICONES[a.id] || Tags;
                  const niv = etat[a.id] || 0;
                  const check = peutAcheter({ amelio: a, magasin: mag, secteurId: retail.secteur, treasury, totalDays });
                  const enChantier = chantiers.some(c => c.amelioId === a.id);
                  const suivant = check.niveau;
                  return (
                    <div key={a.id}
                      onMouseEnter={() => setHoverId(a.id)} onMouseLeave={() => setHoverId(null)}
                      className={`rounded-lg border p-2.5 flex flex-col gap-1 transition-colors ${niv > 0 ? 'border-accent/40 bg-accent/5' : 'border-ink-600 bg-ink-900/30 hover:border-ink-500'}`}
                      data-testid={`amelioration-${a.id}`}>
                      <div className="flex items-center gap-1.5">
                        <Ico className="w-3.5 h-3.5 text-accent" />
                        <span className="font-bold text-xs text-slate-100">{a.nom}</span>
                        <span className="ml-auto flex gap-1">
                          {a.niveaux.map(n => (
                            <span key={n.niveau} className={`w-1.5 h-1.5 rounded-full ${n.niveau <= niv ? 'bg-accent' : 'bg-ink-600'}`} />
                          ))}
                        </span>
                      </div>
                      <p className="text-[10px] text-slate-500 leading-snug">{a.description}</p>
                      {suivant && (check.ok || check.reason === 'fonds') && !enChantier && (
                        <>
                          <p className="text-[10px] text-emerald-400">{fmtEffets(suivant.effets)}</p>
                          <button type="button"
                            onClick={() => {
                              const res = acheterAmelioration({ magasinId: mag.id, amelioId: a.id });
                              if (res?.ok) toast(`🚧 Travaux lancés : ${res.niveau.nom}`, { description: `${formatMoney(res.cout)} — ${res.niveau.travaux_jours} jour(s) de chantier${res.niveau.fermeture_partielle ? ', magasin partiellement fermé' : ''}.` });
                              else if (res?.reason === 'fonds') toast.error('Trésorerie insuffisante');
                            }}
                            disabled={!check.ok}
                            className="mt-auto flex items-center justify-between gap-2 bg-accent/10 border border-accent/40 text-accent px-2 py-1 rounded text-[11px] font-bold hover:bg-accent hover:text-white transition-colors disabled:opacity-40"
                            data-testid={`acheter-${a.id}`}>
                            <span>{suivant.nom}</span>
                            <span className="font-mono whitespace-nowrap">{formatMoney(coutNiveau(suivant, mag.typeId))} · {suivant.travaux_jours} j{entretienNiveau(suivant, mag.typeId) > 0 ? ` · ${formatMoney(entretienNiveau(suivant, mag.typeId))}/mois` : ''}</span>
                          </button>
                        </>
                      )}
                      {enChantier && <p className="text-[10px] text-amber-400 font-bold animate-pulse">🚧 Travaux en cours…</p>}
                      {check.reason === 'dependance' && (
                        <p className="flex items-center gap-1 text-[10px] text-amber-400" data-testid={`verrou-${a.id}`}>
                          <Lock className="w-3 h-3" /> Nécessite {check.dep?.nom} niveau {check.depNiveau}
                        </p>
                      )}
                      {(check.reason === 'type' || check.reason === 'secteur') && (
                        <p className="flex items-center gap-1 text-[10px] text-slate-500" data-testid={`verrou-${a.id}`}>
                          <Lock className="w-3 h-3" /> {RAISONS[check.reason]}
                        </p>
                      )}
                      {check.reason === 'max' && <p className="text-[10px] text-emerald-400">Niveau maximum atteint</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
